import {ReactElement, useContext, useEffect, useMemo, useState} from "react";
import {Body, Cell, Head, HeaderRow, Row, SortableCell, Table} from "@zendeskgarden/react-tables";
import {Col, Grid, Row as GridRow} from "@zendeskgarden/react-grid";
import {Field, Input, Label, Toggle} from "@zendeskgarden/react-forms";
import {Dots, Inline} from "@zendeskgarden/react-loaders";
import {Dropdown, Field as DropdownField, Item, Label as DropdownLabel, Menu, Multiselect, Select} from "@zendeskgarden/react-dropdowns";
import Downshift from "downshift";
import {Tag} from "@zendeskgarden/react-tags";
import {
    PropertyType,
    useGetClientUnitCountsPerPropertyLazyQuery
} from "../../../__generated__/generated_types";
import {AuthContext} from "../../../contexts/AuthContext";
import {formatterEnumToTitle} from "../../../utils/formatters";
import {AdminWrapper} from "../AdminWrapper";
import AdminHeader from "../AdminHeader";
import * as css from "./styles/clientUnitCountStyles.module.scss";
import {COLORS} from "../../../constants/Colors";

const ALL_PROPERTY_TYPES = Object.values(PropertyType);

const MONTH_OF_ACTUALS_OPTIONS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
].map((label, monthIndex) => ({monthIndex, label}));

function getDefaultActualsPeriod(): {yearOfActuals: number; monthOfActuals: number} {
    const now = new Date();
    const currentMonth = now.getMonth();

    return {
        yearOfActuals: now.getFullYear(),
        monthOfActuals: currentMonth
    };
}

function getYearOfActualsOptions(budgetYear: number): number[] {
    const currentYear = new Date().getFullYear();
    const maxYear = Math.min(budgetYear, currentYear);
    const minYear = maxYear - 1;
    const years: number[] = [];

    for (let year = maxYear; year >= minYear; year--) {
        years.push(year);
    }

    return years;
}

const DEFAULT_ACTUALS_PERIOD = getDefaultActualsPeriod();

type SortColumn = {
    key: "name" | "propertyType" | "unitCount" | "isDeactivated" | "isClone";
    direction: "asc" | "desc";
};

type UnitCountRow = {
    propertyId: string;
    propertyType: PropertyType;
    unitCount: number | null | undefined;
    propertyName: string;
    isDeactivated: boolean;
    isClone: boolean;
};

const multiselectStateReducer = (state: any, changes: any) => {
    switch (changes.type) {
        case Downshift.stateChangeTypes.keyDownEnter:
        case Downshift.stateChangeTypes.clickItem:
            return {
                ...changes,
                highlightedIndex: state.highlightedIndex,
                isOpen: true,
                inputValue: "",
            };
        default:
            return changes;
    }
};

export function ClientUnitCount(): ReactElement {
    const {budgetYear} = useContext(AuthContext);
    const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<PropertyType[]>([PropertyType.Standard]);
    const [yearOfActuals, setYearOfActuals] = useState(DEFAULT_ACTUALS_PERIOD.yearOfActuals);
    const [monthOfActuals, setMonthOfActuals] = useState(DEFAULT_ACTUALS_PERIOD.monthOfActuals);
    const [includeDeactivated, setIncludeDeactivated] = useState(false);
    const [includeClone, setIncludeClone] = useState(false);
    const [rows, setRows] = useState<UnitCountRow[]>([]);
    const [totalUnitCount, setTotalUnitCount] = useState(0);
    const [search, setSearch] = useState("");
    const [sortColumn, setSortColumn] = useState<SortColumn>({
        key: "name",
        direction: "asc"
    });

    const [fetchUnitCounts, {data, loading}] = useGetClientUnitCountsPerPropertyLazyQuery({
        fetchPolicy: "no-cache"
    });

    const yearOfActualsOptions = useMemo(() => getYearOfActualsOptions(budgetYear), [budgetYear]);

    useEffect(() => {
        if (!budgetYear) {
            setTotalUnitCount(0);
            setRows([]);
            return;
        }

        if (selectedPropertyTypes.length === 0) {
            setTotalUnitCount(0);
            setRows([]);
            return;
        }

        fetchUnitCounts({
            variables: {
                budgetYear,
                yearOfActuals,
                monthOfActuals,
                propertyTypes: selectedPropertyTypes,
                includeDeactivated,
                includeClone
            }
        });
    }, [budgetYear, fetchUnitCounts, includeClone, includeDeactivated, monthOfActuals, selectedPropertyTypes, yearOfActuals]);


    useEffect(() => {
        if (!data?.getClientUnitCountsPerProperty || loading) {
            return;
        }

        setTotalUnitCount(data.getClientUnitCountsPerProperty.reduce((sum, row) => sum + (row.unitCount ?? 0), 0));
        setRows(data.getClientUnitCountsPerProperty.map(row => ({
            propertyId: row.propertyId,
            propertyType: row.propertyType,
            unitCount: row.unitCount,
            propertyName: row.propertyName,
            isDeactivated: row.isDeactivated,
            isClone: row.isClone
        })));
    }, [data, loading]);

    const filteredRows = useMemo(() => {
        const searchTerm = search.trim().toLowerCase();
        let result = rows;

        if (searchTerm) {
            result = result.filter(row => row.propertyName.toLowerCase().includes(searchTerm));
        }

        return [...result].sort((a, b) => {
            let aValue: string | number = a.propertyName;
            let bValue: string | number = b.propertyName;

            if (sortColumn.key === "propertyType") {
                aValue = a.propertyType;
                bValue = b.propertyType;
            } else if (sortColumn.key === "unitCount") {
                if ((a.unitCount == null || a.unitCount === undefined) && (b.unitCount == null || b.unitCount === undefined)) {
                    return 0;
                }
                if (a.unitCount == null || a.unitCount === undefined) {
                    return 1;
                }
                if (b.unitCount == null || b.unitCount === undefined) {
                    return -1;
                }
                aValue = a.unitCount;
                bValue = b.unitCount;
            } else if (sortColumn.key === "isDeactivated") {
                aValue = a.isDeactivated ? 1 : 0;
                bValue = b.isDeactivated ? 1 : 0;
            } else if (sortColumn.key === "isClone") {
                aValue = a.isClone ? 1 : 0;
                bValue = b.isClone ? 1 : 0;
            }

            if (aValue < bValue) {
                return sortColumn.direction === "asc" ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortColumn.direction === "asc" ? 1 : -1;
            }
            return 0;
        });
    }, [rows, search, sortColumn]);

    function setSort(key: SortColumn["key"], currentDirection: "asc" | "desc" | undefined): void {
        if (currentDirection === "desc") {
            setSortColumn({
                key: "name",
                direction: "asc"
            });
            return;
        }

        setSortColumn({
            key,
            direction: currentDirection === "asc" ? "desc" : "asc"
        });
    }

    return <AdminWrapper>
        <AdminHeader
            title="Client Unit Count"
            subtitle="View total unit counts for the current client, broken down by property."
        />

        <Grid>
            {selectedPropertyTypes.length > 0 && (
                <GridRow>
                    <Col>
                        <div className={css.totalCountCard}>
                            <span className={css.totalCountLabel}>Total Unit Count</span>
                            <span className={css.totalCountValue}>
                                {loading && !data ? "—" : totalUnitCount}
                            </span>
                            <span className={css.totalCountHint}>
                                {loading && !data
                                    ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label={`Loading total unit count`} />
                                    : `${rows.length.toLocaleString()} ${rows.length === 1 ? "property" : "properties"}`}
                            </span>
                        </div>
                    </Col>
                </GridRow>
            )}

            <GridRow>
                <Col>
                    <div className={css.headerActions}>
                        <div className={css.propertyTypeField}>
                            <Dropdown
                                selectedItems={selectedPropertyTypes}
                                onSelect={items => setSelectedPropertyTypes(items as PropertyType[])}
                                downshiftProps={{
                                    defaultHighlightedIndex: 0,
                                    stateReducer: multiselectStateReducer
                                }}
                            >
                                <DropdownField>
                                    <DropdownLabel>Property Types</DropdownLabel>
                                    <Multiselect
                                        placeholder="Select property types"
                                        renderItem={({value, removeValue}) => (
                                            <Tag>
                                                <span>{formatterEnumToTitle(value as PropertyType)}</span>
                                                <Tag.Close onClick={() => removeValue()} />
                                            </Tag>
                                        )}
                                    />
                                </DropdownField>
                                <Menu>
                                    {ALL_PROPERTY_TYPES.map(propertyType => (
                                        <Item key={propertyType} value={propertyType}>
                                            {formatterEnumToTitle(propertyType)}
                                        </Item>
                                    ))}
                                </Menu>
                            </Dropdown>
                        </div>

                        <div className={css.yearOfActualsField}>
                            <Dropdown
                                selectedItem={yearOfActuals}
                                onSelect={item => setYearOfActuals(item as number)}
                            >
                                <DropdownField>
                                    <DropdownLabel>Year of Actuals</DropdownLabel>
                                    <Select>{yearOfActuals}</Select>
                                </DropdownField>
                                <Menu>
                                    {yearOfActualsOptions.map(year => (
                                        <Item key={year} value={year}>
                                            {year}
                                        </Item>
                                    ))}
                                </Menu>
                            </Dropdown>
                        </div>

                        <div className={css.monthOfActualsField}>
                            <Dropdown
                                selectedItem={monthOfActuals}
                                onSelect={item => setMonthOfActuals(item as number)}
                            >
                                <DropdownField>
                                    <DropdownLabel>Month of Actuals</DropdownLabel>
                                    <Select>
                                        {MONTH_OF_ACTUALS_OPTIONS.find(option => option.monthIndex === monthOfActuals)?.label ?? "Select month"}
                                    </Select>
                                </DropdownField>
                                <Menu>
                                    {MONTH_OF_ACTUALS_OPTIONS.map(option => (
                                        <Item key={option.monthIndex} value={option.monthIndex}>
                                            {option.label}
                                        </Item>
                                    ))}
                                </Menu>
                            </Dropdown>
                        </div>
                    </div>
                </Col>
            </GridRow>

            <GridRow>
                <Col>
                    <div className={css.headerActions}>
                        <Field className={css.searchField}>
                            <Input
                                type="text"
                                placeholder="Property Name"
                                value={search}
                                onChange={event => setSearch(event.target.value)}
                                aria-label="Search property list"
                            />
                        </Field>

                        <Field>
                            <Toggle
                                checked={includeDeactivated}
                                onChange={event => setIncludeDeactivated(event.target.checked)}
                            >
                                <Label>Include Deactivated</Label>
                            </Toggle>
                        </Field>

                        <Field>
                            <Toggle
                                checked={includeClone}
                                onChange={event => setIncludeClone(event.target.checked)}
                            >
                                <Label>Include Clone</Label>
                            </Toggle>
                        </Field>
                    </div>
                </Col>
            </GridRow>

            <GridRow>
                <Col>
                    {selectedPropertyTypes.length === 0 ? (
                        <p>Select at least one property type to view unit counts.</p>
                    ) : loading && !data ? (
                        <Dots size={32} />
                    ) : (
                        <Table className={css.table}>
                                <Head>
                                    <HeaderRow>
                                        <SortableCell
                                            onClick={() => setSort("name", sortColumn.key === "name" ? sortColumn.direction : undefined)}
                                            sort={sortColumn.key === "name" ? sortColumn.direction : undefined}
                                        >
                                            Property
                                        </SortableCell>
                                        <SortableCell
                                            onClick={() => setSort("propertyType", sortColumn.key === "propertyType" ? sortColumn.direction : undefined)}
                                            sort={sortColumn.key === "propertyType" ? sortColumn.direction : undefined}
                                        >
                                            Property Type
                                        </SortableCell>
                                        <SortableCell
                                            onClick={() => setSort("unitCount", sortColumn.key === "unitCount" ? sortColumn.direction : undefined)}
                                            sort={sortColumn.key === "unitCount" ? sortColumn.direction : undefined}
                                        >
                                            Unit Count
                                        </SortableCell>
                                        <SortableCell
                                            onClick={() => setSort("isDeactivated", sortColumn.key === "isDeactivated" ? sortColumn.direction : undefined)}
                                            sort={sortColumn.key === "isDeactivated" ? sortColumn.direction : undefined}
                                        >
                                            Is Deactivated
                                        </SortableCell>
                                        <SortableCell
                                            onClick={() => setSort("isClone", sortColumn.key === "isClone" ? sortColumn.direction : undefined)}
                                            sort={sortColumn.key === "isClone" ? sortColumn.direction : undefined}
                                        >
                                            Is Clone
                                        </SortableCell>
                                    </HeaderRow>
                                </Head>
                                <Body>
                                    {filteredRows.length === 0 ? (
                                        <Row>
                                            <Cell colSpan={5}>No properties found for the selected filters.</Cell>
                                        </Row>
                                    ) : filteredRows.map(row => (
                                        <Row key={row.propertyId}>
                                            <Cell>{row.propertyName}</Cell>
                                            <Cell>{formatterEnumToTitle(row.propertyType)}</Cell>
                                            <Cell isMinimum>{row.unitCount != null && row.unitCount !== undefined ? row.unitCount.toLocaleString() : "—"}</Cell>
                                            <Cell>{row.isDeactivated ? "Yes" : "No"}</Cell>
                                            <Cell>{row.isClone ? "Yes" : "No"}</Cell>
                                        </Row>
                                    ))}
                                </Body>
                            </Table>
                    )}
                </Col>
            </GridRow>
        </Grid>
    </AdminWrapper>;
}
